import { useEffect, useState } from 'react';

const checks = new Map<string, { expires: number; result: Promise<string | null> }>();

export function applyPreviewOrigin(previewUrl: string, origin: string): string {
  const preview = new URL(previewUrl);
  const domain = new URL(origin);
  if (domain.protocol !== 'https:') return previewUrl;
  return domain.origin + preview.pathname + preview.search + preview.hash;
}

export async function resolvePreviewDomain(domain: string): Promise<string | null> {
  const existing = checks.get(domain);
  if (existing && existing.expires > Date.now()) return existing.result;
  const result = (async () => {
    try {
      const response = await fetch(`/api/preview-domain?domain=${encodeURIComponent(domain)}`, { signal: AbortSignal.timeout(5000) });
      if (!response.ok) return null;
      const data = await response.json();
      return typeof data.origin === 'string' && new URL(data.origin).protocol === 'https:' ? data.origin : null;
    } catch { return null; }
  })();
  if (checks.size >= 500) checks.delete(checks.keys().next().value!);
  checks.set(domain, { expires: Date.now() + 30000, result });
  return result;
}

export function usePreviewUrl(previewUrl: string, domain?: string | null): string {
  const [resolved, setResolved] = useState<{ domain: string; origin: string } | null>(null);
  useEffect(() => {
    let current = true;
    if (domain && previewUrl) {
      void resolvePreviewDomain(domain).then(origin => {
        if (current) setResolved(origin ? { domain, origin } : null);
      });
    }
    return () => { current = false; };
  }, [domain, previewUrl]);
  if (resolved && resolved.domain === domain) {
    try { return applyPreviewOrigin(previewUrl, resolved.origin); } catch { /* Keep API fallback. */ }
  }
  return previewUrl;
}
